import { useCallback } from 'react';
import { usePrefs } from '../../lib/prefs.ts';
import { SHORTCUT_HELP } from '../../lib/shortcuts.ts';
import type { ShortcutHelp } from '../../lib/shortcuts.ts';
import { usePageShortcuts } from '../../lib/pageShortcuts.ts';
import { Dialog } from '@orgus/ui';
import { Kbd } from '@orgus/ui';

/** "Pick an option (1 to 9)" on the key 1: the keys show the whole range, the label drops it. */
const RANGE = /\s*\((\S+) to (\S+)\)$/;

function Keys({ s }: { s: ShortcutHelp }) {
  const range = s.keys.length === 1 ? RANGE.exec(s.label) : null;
  if (range && range[1] === s.keys[0]) {
    return (
      <span className="inline-flex items-center gap-1">
        <Kbd keys={[range[1]]} />
        <span className="text-caption text-fg-subtle">to</span>
        <Kbd keys={[range[2]]} />
      </span>
    );
  }
  return <Kbd keys={s.keys} then={s.then} />;
}

function label(s: ShortcutHelp): string {
  return s.keys.length === 1 && RANGE.exec(s.label)?.[1] === s.keys[0] ? s.label.replace(RANGE, '') : s.label;
}

/** One shortcut: its name and its keys, on a quiet line that shows in both themes. */
function Row({ s }: { s: ShortcutHelp }) {
  return (
    <li className="flex min-h-7 items-center justify-between gap-4 border-b border-fg/8 py-1 text-ui last:border-b-0">
      <span className="text-fg-muted">{label(s)}</span>
      <Keys s={s} />
    </li>
  );
}

function Group({ title, items }: { title: string; items: readonly ShortcutHelp[] }) {
  return (
    <section>
      <h3 className="mb-1 text-caption font-medium text-fg-subtle">{title}</h3>
      <ul className="flex flex-col">
        {items.map((s) => (
          <Row key={s.label} s={s} />
        ))}
      </ul>
    </section>
  );
}

const of = (g: ShortcutHelp['group']) => SHORTCUT_HELP.filter((s) => s.group === g);

/**
 * The ? overlay: every keyboard shortcut. The open page's own keys first, then two columns that
 * each stack on their own (General and Actions beside Navigation), so a short group never leaves a
 * hole beside a long one. When the sheet scrolls, its foot fades while more rows hide below.
 */
export function ShortcutsHelp() {
  const open = usePrefs((s) => s.helpOpen);
  const setOpen = usePrefs((s) => s.setHelpOpen);
  // The open page's own keys (the Inbox's), first, under its name.
  const page = usePageShortcuts((s) => s.page);
  // The dialog body is the scroller: mark it while rows hide below its foot (a callback ref, since
  // the dialog's portal mounts its content after this component).
  const watchBody = useCallback((el: HTMLDivElement | null) => {
    const body = el?.parentElement;
    if (!el || !body) return;
    const mark = () => body.toggleAttribute('data-fade-end', body.scrollHeight - body.clientHeight - body.scrollTop > 1);
    mark();
    body.addEventListener('scroll', mark, { passive: true });
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(mark);
    ro?.observe(body);
    ro?.observe(el);
    return () => {
      body.removeEventListener('scroll', mark);
      ro?.disconnect();
    };
  }, []);

  return (
    <Dialog
      open={open}
      onOpenChange={setOpen}
      title="Keyboard shortcuts"
      size="lg"
      description="Single keys work anywhere except while typing in a field."
      bodyClassName="data-fade-end:mask-b-from-85%"
    >
      <div ref={watchBody} className="flex flex-col gap-6 pb-2">
        {page && (
          <section>
            <h3 className="mb-1 text-caption font-medium text-fg-subtle">{page.title}</h3>
            <ul className="grid sm:grid-cols-2 sm:gap-x-8">
              {page.items.map((s) => (
                <Row key={s.label} s={s} />
              ))}
            </ul>
          </section>
        )}
        <div className="grid items-start gap-x-8 gap-y-6 sm:grid-cols-2">
          <div className="flex flex-col gap-6">
            <Group title="General" items={of('General')} />
            <Group title="Actions" items={of('Actions')} />
          </div>
          <Group title="Navigation" items={of('Navigation')} />
        </div>
      </div>
    </Dialog>
  );
}
